import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Container, InputAdornment, Snackbar, TextField } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons'
import LoadingIndicator from '../../common/LoadingIndicator'
import useFetch from '../../hooks/useFetch'
import usePermissions from '../../hooks/usePermissions'
import { urlApi } from '../../config'
import { FIELDS, GROUPS } from './constants'
import { buildPayload, describeField, draftFromConfig, normalizeSearch } from './utils'
import RateTable from './RateTable'
import ConfirmChangesDialog from './ConfirmChangesDialog'
import './pricing.css'

const MUTED = 'text-gray-500 dark:text-gray-400'

const RATE_COUNT = GROUPS.reduce((total, group) => total + group.rows.length, 0)

export default function Pricing() {
  const { loading, data, responseOk, refetch } = useFetch(`${urlApi}/admin/pricing`)
  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('edit-pricing')
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState({})
  const [query, setQuery] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success', undo: false })
  const discardedDraft = useRef(null)

  useEffect(() => {
    if (responseOk && data?.price_per_km && data?.max_price_multiplier) {
      const config = {
        price_per_km: data.price_per_km,
        max_price_multiplier: data.max_price_multiplier
      }
      setSaved(config)
      setDraft(draftFromConfig(config))
    }
  }, [responseOk, data])

  const fieldStates = useMemo(
    () =>
      saved
        ? Object.fromEntries(FIELDS.map(field => [field.id, describeField(field, saved, draft)]))
        : {},
    [saved, draft]
  )

  const changes = FIELDS.filter(field => fieldStates[field.id]?.changed).map(field => ({
    field,
    state: fieldStates[field.id]
  }))
  const errorCount = changes.filter(change => change.state.error).length
  const hasChanges = changes.length > 0

  useEffect(() => {
    if (!hasChanges) return
    const warnBeforeLeaving = event => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeLeaving)
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving)
  }, [hasChanges])

  const visibleGroups = useMemo(() => {
    const term = normalizeSearch(query)
    if (!term) return GROUPS
    return GROUPS.map(group => ({
      ...group,
      rows: group.rows.filter(row =>
        normalizeSearch(`${group.label} ${group.currency} ${row.label} ${row.subtitle ?? ''}`).includes(
          term
        )
      )
    })).filter(group => group.rows.length > 0)
  }, [query])

  const payload = useMemo(() => (saved ? buildPayload(saved, draft) : null), [saved, draft])

  if (!saved) {
    if (loading) {
      return (
        <div className='h-full flex items-center justify-center'>
          <LoadingIndicator />
        </div>
      )
    }
    return (
      <Container maxWidth='md' sx={{ py: 4 }}>
        <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm p-6 flex flex-col items-start gap-3'>
          <p className='text-base font-semibold text-gray-800 dark:text-gray-100'>
            No se pudo cargar la configuración de precios
          </p>
          <p className={`text-sm ${MUTED}`}>
            Puede ser un problema de conexión o que tu usuario no tenga acceso a esta sección.
          </p>
          <button
            className='px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity'
            onClick={refetch}
          >
            Reintentar
          </button>
        </div>
      </Container>
    )
  }

  const closeToast = () => setToast(prev => ({ ...prev, open: false }))

  const handleChange = (id, value) => setDraft(prev => ({ ...prev, [id]: value }))

  const handleDiscard = () => {
    discardedDraft.current = draft
    setDraft(draftFromConfig(saved))
    setToast({ open: true, message: 'Cambios descartados', severity: 'info', undo: true })
  }

  const handleUndoDiscard = () => {
    if (discardedDraft.current) setDraft(discardedDraft.current)
    discardedDraft.current = null
    closeToast()
  }

  const handleSaved = () => {
    setSaved(payload)
    setDraft(draftFromConfig(payload))
    setSaving(false)
    setConfirmOpen(false)
    setToast({ open: true, message: 'Precios actualizados', severity: 'success', undo: false })
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        height: 'calc(100vh - 4rem)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        width: '100%',
        maxWidth: '1080px',
        px: { xs: 2, md: 3 }
      }}
    >
      <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm p-4 mt-6 mb-4 flex flex-wrap items-center gap-3'>
        <TextField
          size='small'
          placeholder='Buscar ruta o país'
          value={query}
          onChange={e => setQuery(e.target.value)}
          className='w-full sm:w-72'
          inputProps={{ 'aria-label': 'Buscar ruta o país' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={faMagnifyingGlass} className={`text-sm ${MUTED}`} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position='end'>
                <button
                  className={`w-7 h-7 -mr-1 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover ${MUTED}`}
                  onClick={() => setQuery('')}
                  aria-label='Limpiar búsqueda'
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </InputAdornment>
            ) : null
          }}
        />
        <div className='flex flex-wrap items-center gap-3 sm:ml-auto'>
          {!canEdit && (
            <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-light_gray dark:bg-dk-raised text-xs font-medium text-gray-600 dark:text-gray-300'>
              <FontAwesomeIcon icon={faLock} aria-hidden />
              Solo lectura
            </span>
          )}
          <span className={`text-sm ${MUTED}`}>
            {RATE_COUNT} tarifas · {GROUPS.length} monedas
          </span>
        </div>
      </div>

      {visibleGroups.length > 0 ? (
        <RateTable
          groups={visibleGroups}
          draft={draft}
          saved={saved}
          fieldStates={fieldStates}
          canEdit={canEdit}
          onChange={handleChange}
        />
      ) : (
        <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm px-6 py-10 text-center'>
          <p className='text-sm font-medium text-gray-800 dark:text-gray-100'>
            Ninguna ruta coincide con “{query}”
          </p>
          <button className='mt-2 text-sm text-primary hover:underline' onClick={() => setQuery('')}>
            Ver todas las rutas
          </button>
        </div>
      )}

      <div className='flex-1 min-h-[1.5rem]' />

      {canEdit && hasChanges && (
        <div className='sticky bottom-0 z-20 pb-4 pricing-bar-in'>
          <div
            role='region'
            aria-label='Cambios sin guardar'
            className='flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-white dark:bg-dk-raised shadow-[0_8px_30px_-6px_rgba(0,0,0,0.25)] ring-1 ring-gray-200 dark:ring-dk-border px-4 py-3'
          >
            <span aria-hidden className='w-2 h-2 rounded-full bg-primary' />
            <p className='text-sm font-medium text-gray-800 dark:text-gray-100'>
              {changes.length} {changes.length === 1 ? 'cambio sin guardar' : 'cambios sin guardar'}
            </p>
            {errorCount > 0 && (
              <p className='text-sm text-red-600 dark:text-red-400'>
                {errorCount === 1 ? 'Corregí el valor marcado' : `Corregí los ${errorCount} valores marcados`}
              </p>
            )}
            <div className='ml-auto flex gap-2'>
              <button
                className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-hover text-black dark:text-gray-100 hover:opacity-90 transition-opacity'
                onClick={handleDiscard}
              >
                Descartar
              </button>
              <button
                className='px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity'
                onClick={() => {
                  setSaveError(false)
                  setConfirmOpen(true)
                }}
                disabled={errorCount > 0}
              >
                Revisar y guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmChangesDialog
        open={confirmOpen}
        changes={changes}
        payload={payload}
        saving={saving}
        saveError={saveError}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setSaveError(false)
          setSaving(true)
        }}
        onSaved={handleSaved}
        onSaveError={() => {
          setSaving(false)
          setSaveError(true)
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.undo ? 6000 : 3000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') closeToast()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant='filled'
          onClose={closeToast}
          action={
            toast.undo ? (
              <button
                className='px-2 py-1 text-sm font-semibold uppercase tracking-wide hover:opacity-80'
                onClick={handleUndoDiscard}
              >
                Deshacer
              </button>
            ) : undefined
          }
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
